/**
 * Startup sanity check for REACT_APP_BASE_URL.
 *
 * The base URL is inlined by webpack at build time into ~55 separate call
 * sites, so it cannot be corrected at runtime - a wrong value silently breaks
 * data loading on every route. The two failure modes below are invisible on the
 * developer's own machine and only show up on another device, where they
 * surface as an unhelpful "Network error, can't fetch" toast:
 *
 *   1. Base URL points at localhost / 127.0.0.1 while the app is being viewed
 *      from a phone or another machine. Those hosts resolve to the *viewing*
 *      device, so nothing is listening.
 *   2. Base URL is http:// while the page is served over https://. Browsers
 *      block the request as mixed content before it leaves the tab.
 *
 * This logs a loud, specific diagnosis instead of leaving it to be rediscovered.
 * Console-only and dev-only: it never blocks rendering or alters requests.
 */
const LOOPBACK = new Set(["localhost", "127.0.0.1", "[::1]", "::1"]);

export default function checkApiBaseUrl() {
  if (process.env.NODE_ENV === "production") return;
  if (typeof window === "undefined") return;

  const baseUrl = process.env.REACT_APP_BASE_URL;
  const pageHost = window.location.hostname;

  if (!baseUrl) {
    console.error(
      "[config] REACT_APP_BASE_URL is not set. Requests will fall back to " +
        "http://localhost:8000 (or resolve to 'undefined/...'). Set it in .env " +
        "and restart the dev server."
    );
    return;
  }

  let apiHost;
  let apiProtocol;
  try {
    ({ hostname: apiHost, protocol: apiProtocol } = new URL(baseUrl));
  } catch {
    console.error(`[config] REACT_APP_BASE_URL is not a valid URL: "${baseUrl}"`);
    return;
  }

  if (LOOPBACK.has(apiHost) && !LOOPBACK.has(pageHost)) {
    console.error(
      `[config] REACT_APP_BASE_URL is "${baseUrl}" but this page is being served ` +
        `from "${pageHost}". On this device ${apiHost} points at the device ` +
        "itself, so every API call will fail with a network error on all " +
        "routes.\nFix: set REACT_APP_BASE_URL to this machine's LAN address " +
        "(e.g. http://192.168.x.y:8000) or to the deployed API, then restart " +
        "the dev server (.env is read at startup only).\nNote: dotenv is " +
        "last-wins - a second uncommented REACT_APP_BASE_URL overrides the first."
    );
  }

  if (window.location.protocol === "https:" && apiProtocol === "http:") {
    console.error(
      `[config] Mixed content: the page is https but REACT_APP_BASE_URL is ` +
        `"${baseUrl}". The browser will block every API request. Use an https ` +
        "backend URL."
    );
  }
}
